import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  linkedSignal,
  signal,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';

import { injectGetAllVendorBalanceSheetsQuery } from '@models/balance-sheet-accounts/get-all-vendor-balance-sheets.query';

import { FileManagerComponent } from '@shared/components/file-manager/file-manager.component';
import { File } from '@shared/components/file-manager/state/file.model';
import { FileViewerComponent } from '@shared/components/file-viewer/file-viewer.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { InputComponent } from '@shared/components/input/input.component';
import {
  ConfirmationActionModalComponent,
  ConfirmationActionModalData,
} from '@shared/components/modals/confirmation-action-modal/confirmation-action-modal.components';
import { CustomOverlayRef } from '@shared/components/overlay/custom-overlay-ref';
import { GqlService } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { MainQuery } from '@shared/store/main/main.query';

import { NewManageBalanceSheetsAccountsComponent } from './components/balance-sheet-account-grid/new-manage-balance-sheets-accounts.component';
import {
  injectBatchUpdateEntitySettingsMutation,
  injectProcessBudgetAttributeChanges,
} from './mutations/balance-sheet-account-mutations';

export type BalanceSheetAccountDetail = {
  attributes: Record<string, string>;
  PROGRAM: string;
  PROJECT: string;
};

export type BalanceSheetEntitySettingsPayload = {
  // Using an index signature for account types like ACCOUNTS_PAYABLE, etc.
  // The value can be BalanceSheetAccountDetail for account types,
  // or string/string[] for NOTES/SUPPORTING_DOCUMENT_S3_BUCKET_KEYS (handled by the explicit properties above),
  // or undefined if an account type is not present.
  [accountType: string]: BalanceSheetAccountDetail | string | string[] | undefined;
  NOTES?: string;
  SUPPORTING_DOCUMENT_S3_BUCKET_KEYS?: string[];
};

export interface NewBudgetAttribute {
  colID: string;
  name: string;
}

export interface PendingGridChanges {
  entitySettingsToSave: BalanceSheetEntitySettingsPayload;
  newBudgetAttributes: NewBudgetAttribute[];
  removedBudgetAttributes: RemovedBudgetAttribute[];
  renamedBudgetAttributes: RenamedBudgetAttribute[];
}

export interface RemovedBudgetAttribute {
  name: string;
}

export interface RenamedBudgetAttribute {
  name: string;
  new_name: string;
}

const emptyRowMessage = 'Fill out at least 1 attribute to Save';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NewManageBalanceSheetsAccountsComponent,
    InputComponent,
    IconComponent,
    FileManagerComponent,
    FileViewerComponent,
    FormsModule,
  ],
  selector: 'aux-new-manage-balance-sheets-accounts-modal',
  template: `
    <aux-new-manage-balance-sheets-accounts />
    <div class="mt-4 grid h-60 w-[1200px] grid-cols-3">
      <div class="col-span-2 mr-8 flex flex-col items-start justify-start space-y-3">
        <div class="flex items-center justify-start space-x-1">
          <aux-icon [name]="'Message'" />
          <span class="font-bold">
            <span class="mr-1">Notes</span>
            <span class="text-aux-error">*</span>
          </span>
        </div>
        <aux-input
          id="notes"
          class="w-full pl-1 text-xs"
          placeholder="Enter notes here..."
          validators="required"
          [inAppBudgetStyle]="true"
          [textArea]="true"
          resize="none"
          [errMessage]="'Notes*'"
          [ngModel]="notes()"
          (ngModelChange)="notes.set($event)"
        />
      </div>
      <div class="col-span-1 flex w-full flex-col space-y-3">
        <div class="flex flex-row items-center justify-start space-x-2">
          <span class="font-bold">Supporting Documents</span>
          <span class="italic">(optional)</span>
        </div>
        <div class="w-full">
          <aux-file-manager #manager class="h-32" [fetchFilesOnInit]="false" [eager]="false" />
          <div class="mt-4 max-h-[42px] overflow-y-auto">
            <aux-file-viewer
              [fileManager]="manager"
              [disableFirstFileMargin]="true"
              [onlyShowUploaded]="false"
              [type]="'inAppBudget'"
            />
          </div>
        </div>
      </div>
    </div>
  `,
})
export class NewManageBalanceSheetsAccountsModalComponent {
  gqlService = inject(GqlService);
  mainQuery = inject(MainQuery);
  overlayService = inject(OverlayService);

  gridComp = viewChild.required(NewManageBalanceSheetsAccountsComponent);

  allVendorIds = computed(() =>
    this.gridComp()
      .vendors()
      .map((v) => v.id)
  );

  allVendorSettingsQuery = injectGetAllVendorBalanceSheetsQuery({
    enabled: false,
    vendorIdsSignal: this.allVendorIds,
  });
  batchUpdateEntitySettingsMutation = injectBatchUpdateEntitySettingsMutation();
  confirmApplyToAllModal = injectConfirmationModalForApplyToAll();
  confirmAttributeChangesModal = injectAttributeChangesConfirmationModal();
  processBudgetAttributeChangesMutation = injectProcessBudgetAttributeChanges();

  headerHasError = signal(false);
  notes = signal('');
  saveLoading = signal(false);

  canSave = computed(() => {
    const headerError = this.gridComp().headerContext.headerErrorTooltip();
    const isGridDirty = this.gridComp().isDirty();
    const isNotesDirty = this.notes().trim().length > 0;

    return isNotesDirty && isGridDirty && !headerError;
  });
  loading = computed(() => {
    return this.saveLoading() || this.batchUpdateEntitySettingsMutation.isPending();
  });
  saveDisabledReason = computed(() => {
    const headerError = this.gridComp().headerContext.headerErrorTooltip();

    if (headerError) return headerError;

    if (!this.gridComp().isDirty()) return emptyRowMessage;

    if (!this.notes().trim()) return 'Please enter notes.';

    return '';
  });

  hasUnsavedChanges = linkedSignal(() => {
    const gridDirty = this.gridComp().isDirty();
    const notesDirty = this.notes().trim().length > 0;

    return gridDirty || notesDirty;
  });

  fileManager = viewChild.required(FileManagerComponent);

  customOverlayRef = inject<
    CustomOverlayRef<
      unknown,
      {
        programOptions: string[];
        projectOptions: string[];
      }
    >
  >(CustomOverlayRef);

  batchUpdateEntitySettings = async (params: {
    allVendorIds: string[];
    allVendorSettings: Record<string, BalanceSheetEntitySettingsPayload>;
    applyToAll: boolean;
    notes: string;
    pendingChanges: PendingGridChanges;
    selectedVendorId: string;
    supportingDocumentS3BucketKeys: string[];
  }) => {
    try {
      await this.batchUpdateEntitySettingsMutation.mutateAsync(params);

      return true;
    } catch (err) {
      this.overlayService.error(
        'An error occurred during the save process. Please check the console or try again.'
      );
      console.error('Error in batchUpdateEntitySettings:', err);
      this.batchUpdateEntitySettingsMutation.reset();

      return false;
    }
  };

  constructor() {
    effect(() => {
      const gridComp = this.gridComp();

      if (!gridComp) return;

      if (this.customOverlayRef.data) {
        gridComp.programProjectContext.defaultProgramOptions.set(
          this.customOverlayRef.data.programOptions
        );
        gridComp.programProjectContext.defaultProjectOptions.set(
          this.customOverlayRef.data.projectOptions
        );
      }
    });

    this.customOverlayRef.canDeactivate = async () => {
      if (this.hasUnsavedChanges()) {
        const result = this.overlayService.openUnsavedChangesConfirmation();
        const event = await firstValueFrom(result.afterClosed$);

        return !!event?.data;
      }

      return true;
    };
  }

  getBucketKey = (bucketKey: string): string => {
    const trialId = this.mainQuery.getSelectedTrial()?.id;
    const vendorId = this.gridComp().selectedVendor();

    return `trials/${trialId}/vendors/${vendorId}/budget/in-app-budget-edit/${bucketKey}`;
  };

  handleSave = async () => {
    if (!this.validateSave()) return;

    try {
      this.saveLoading.set(true);

      const gridComp = this.gridComp();
      const pendingChanges = gridComp.getPendingChanges();
      const applyToAll = gridComp.isApplyToAllVendors();

      // Show confirmation for attribute changes
      const hasAttributeChanges =
        pendingChanges.newBudgetAttributes.length > 0 ||
        pendingChanges.removedBudgetAttributes.length > 0 ||
        pendingChanges.renamedBudgetAttributes.length > 0;

      if (applyToAll) {
        const confirmed = await this.confirmApplyToAllModal();

        if (!confirmed) {
          this.saveLoading.set(false);

          return;
        }
      }

      if (hasAttributeChanges) {
        const confirmed = await this.confirmAttributeChangesModal();

        if (!confirmed) {
          this.saveLoading.set(false);

          return;
        }
      }

      const filesSaved = await this.saveFiles();

      if (!filesSaved) {
        this.saveLoading.set(false);

        return;
      }

      const supportingDocumentS3BucketKeys = this.prepareFileData();
      const selectedVendorId = gridComp.selectedVendor();

      const { allVendorIds, allVendorSettings } = await this.prepareVendorSettings();

      const batchUpdateSuccess = await this.batchUpdateEntitySettings({
        allVendorIds,
        allVendorSettings,
        applyToAll,
        notes: this.notes(),
        pendingChanges,
        selectedVendorId,
        supportingDocumentS3BucketKeys,
      });

      if (!batchUpdateSuccess) {
        this.saveLoading.set(false);

        return;
      }

      const attributeChangeSuccess = await this.processAttributeChanges(
        pendingChanges,
        selectedVendorId,
        supportingDocumentS3BucketKeys
      );

      if (!attributeChangeSuccess) {
        this.saveLoading.set(false);

        return;
      }

      this.handleSaveSuccess();
    } catch (outerError) {
      this.handleSaveError(outerError);
    } finally {
      this.handleSaveCleanup();
    }
  };

  handleSaveCleanup = () => {
    this.saveLoading.set(false);
    this.batchUpdateEntitySettingsMutation.reset();
    this.processBudgetAttributeChangesMutation.reset();
  };

  handleSaveError = (error: unknown) => {
    console.error('Critical error in handleSave wrapper:', error);
    this.overlayService.error('A critical error occurred. Please try again.');
  };

  handleSaveSuccess = () => {
    this.overlayService.success('All changes saved successfully!');
    this.gridComp().markClean();
    this.hasUnsavedChanges.set(false);
    this.customOverlayRef.close(true);
  };

  prepareFileData = () => {
    const fileManager = this.fileManager();
    const filesToUpload: File[] = fileManager ? fileManager.fileQuery.getAll() : [];

    return filesToUpload.map((file) => file.bucket_key);
  };

  prepareVendorSettings = async () => {
    const gridComp = this.gridComp();
    const vendors = gridComp.vendors();
    const allVendorIds = vendors.map((v) => v.id);

    const query = await this.allVendorSettingsQuery.refetch();

    return {
      allVendorIds,
      allVendorSettings: (query.data ?? []).reduce(
        (acc, s) => {
          acc[s.entity_id] = s.settings ? JSON.parse(s.settings) : {};

          return acc;
        },
        {} as Record<string, BalanceSheetEntitySettingsPayload>
      ),
    };
  };

  processAttributeChanges = async (
    pendingChanges: PendingGridChanges,
    vendorId: string,
    supportingDocumentS3BucketKeys: string[]
  ): Promise<boolean> => {
    const hasAttributeChanges =
      pendingChanges.newBudgetAttributes.length > 0 ||
      pendingChanges.removedBudgetAttributes.length > 0 ||
      pendingChanges.renamedBudgetAttributes.length > 0;

    if (!hasAttributeChanges) return true;
    try {
      await this.processBudgetAttributeChangesMutation.mutateAsync({
        notes: this.notes(),
        pendingChanges,
        supporting_document_s3_bucket_keys: supportingDocumentS3BucketKeys,
        transaction_id: '',
        vendor_id: vendorId,
      });

      return true;
    } catch (error) {
      console.error('Error in processAttributeChanges:', error);

      return false;
    }
  };

  saveFiles = async (): Promise<boolean> => {
    const fileManager = this.fileManager();

    if (!fileManager) {
      this.overlayService.error('File manager not available.');

      return false;
    }

    const filesToUpload: File[] = fileManager.fileQuery.getAll();

    if (filesToUpload.length === 0) {
      return true;
    }

    filesToUpload.forEach((file) => {
      const newFullBucketKey = this.getBucketKey(file.bucket_key);

      fileManager.fileStore.update(file.id, { ...file, bucket_key: newFullBucketKey });
    });

    try {
      const success = await fileManager.fileService.uploadFiles({ metadata: { admin: '1' } });

      if (!success) {
        this.overlayService.error('Error uploading supporting documents.');

        return false;
      }

      return true;
    } catch (err) {
      console.error(err);
      this.overlayService.error('An unexpected error occurred while uploading documents.');

      return false;
    }
  };

  validateSave = (): boolean => {
    if (!this.canSave()) {
      this.overlayService.error(
        'Cannot save. Please ensure all required fields are filled and changes are made.'
      );

      return false;
    }

    return true;
  };
}

export function injectManageBalanceSheetsAccountsModal() {
  const overlayService = inject(OverlayService);

  return ({
    programOptions = [],
    projectOptions = [],
  }: {
    programOptions: string[];
    projectOptions: string[];
  }) => {
    return overlayService.openPopup<
      {
        programOptions: string[];
        projectOptions: string[];
      },
      unknown,
      NewManageBalanceSheetsAccountsModalComponent
    >({
      content: NewManageBalanceSheetsAccountsModalComponent,
      data: {
        programOptions,
        projectOptions,
      },
      settings: {
        header: 'Manage Balance Sheet Accounts',
        primaryButton: {
          action: (instance) => instance?.handleSave?.(),
          disabled: (instance) => !instance?.canSave?.(),
          label: 'Save',
          tooltip: (instance) => instance?.saveDisabledReason?.() ?? '',
        },
      },
    });
  };
}

const injectAttributeChangesConfirmationModal = () => {
  const overlayService = inject(OverlayService);

  return async () => {
    const ref = overlayService.openPopup<
      ConfirmationActionModalData,
      boolean,
      ConfirmationActionModalComponent
    >({
      data: {
        hideConfirmationHint: true,
        message:
          'Updates to attribute columns will apply to every vendor budget and balance sheet accounts.<div class="mt-4">Are you sure you want to proceed?</div>',
        skipKeywordConfirmation: true,
      },
      modal: ConfirmationActionModalComponent,
      settings: {
        header: 'Update Attributes Confirmation',
        primaryButton: {
          label: 'Confirm',
        },
      },
    });

    const { data: refData } = await firstValueFrom(ref.afterClosed$);

    return !!refData;
  };
};

const injectConfirmationModalForApplyToAll = () => {
  const overlayService = inject(OverlayService);

  return async () => {
    const ref = overlayService.openPopup<
      ConfirmationActionModalData,
      boolean,
      ConfirmationActionModalComponent
    >({
      data: {
        hideConfirmationHint: true,
        message:
          'You have selected “Apply to all Vendor Contracts”. Saving will apply values to all Vendor Contracts overriding any values previously entered for Balance Sheet Accounts. The PO # field is specific to each Vendor Contract and will not be applied across vendors. <div class="mt-4">Are you sure you want to proceed?</div>',
        skipKeywordConfirmation: true,
      },
      modal: ConfirmationActionModalComponent,
      settings: {
        header: 'Apply to All Vendor Contracts',
        primaryButton: {
          label: 'OK',
        },
      },
    });

    const { data: refData } = await firstValueFrom(ref.afterClosed$);

    return !!refData;
  };
};
